import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import SwitchField from '../../components/common/SwitchField';
import {
  getPaymentDisputeDetail,
  savePaymentDisputeEvidence,
  searchPaymentDisputes,
  submitPaymentDisputeEvidence,
  syncPaymentDispute,
  uploadPaymentDisputeEvidenceAttachment,
} from '../../services/paymentOperationsService';

const ROWS_PER_PAGE = 25;

const formatUtcDateTime = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
};

const formatAmount = (minorUnits, currency) => {
  if (minorUnits == null) {
    return '—';
  }

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(minorUnits / 100);
};

const PaymentDisputesSection = ({ refreshToken, onFeedback }) => {
  const { t } = useTranslation();
  const [requiresActionOnly, setRequiresActionOnly] = useState(true);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedDisputeId, setSelectedDisputeId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [evidenceForm, setEvidenceForm] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  const loadDisputes = useCallback(async () => {
    setLoading(true);

    try {
      const result = await searchPaymentDisputes({
        requiresActionOnly,
        pageIndex: page - 1,
        itemsPerPage: ROWS_PER_PAGE,
      });
      setRows(result.data);
      setTotal(result.totalItems);
    } catch (err) {
      console.error('Failed to load payment disputes:', err);
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.error.loading'),
      });
    } finally {
      setLoading(false);
    }
  }, [onFeedback, page, requiresActionOnly, t]);

  useEffect(() => {
    loadDisputes();
  }, [loadDisputes, refreshToken]);

  const openDetail = async (row) => {
    setSelectedDisputeId(row.id);
    setDetail(null);
    setDetailLoading(true);

    try {
      const disputeDetail = await getPaymentDisputeDetail(row.id);
      setDetail(disputeDetail);
      setEvidenceForm(disputeDetail.evidenceDraft || {});
    } catch (err) {
      console.error('Failed to load dispute detail:', err);
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.error.loading'),
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (disputeId) => {
    const disputeDetail = await getPaymentDisputeDetail(disputeId);
    setDetail(disputeDetail);
    setEvidenceForm(disputeDetail.evidenceDraft || {});
    return disputeDetail;
  };

  const handleSaveEvidence = async () => {
    if (!selectedDisputeId) {
      return;
    }

    setActionLoading(true);
    try {
      await savePaymentDisputeEvidence(selectedDisputeId, evidenceForm);
      await refreshDetail(selectedDisputeId);
      onFeedback?.({
        severity: 'success',
        message: t('paymentOperations.disputes.saveEvidenceSuccess'),
      });
    } catch (err) {
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.disputes.saveEvidenceError'),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitEvidence = async () => {
    if (!selectedDisputeId) {
      return;
    }

    setActionLoading(true);
    try {
      await submitPaymentDisputeEvidence(selectedDisputeId);
      await refreshDetail(selectedDisputeId);
      await loadDisputes();
      onFeedback?.({
        severity: 'success',
        message: t('paymentOperations.disputes.submitEvidenceSuccess'),
      });
    } catch (err) {
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.disputes.submitEvidenceError'),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSync = async () => {
    if (!selectedDisputeId) {
      return;
    }

    setActionLoading(true);
    try {
      await syncPaymentDispute(selectedDisputeId);
      await refreshDetail(selectedDisputeId);
      await loadDisputes();
      onFeedback?.({
        severity: 'success',
        message: t('paymentOperations.disputes.syncSuccess'),
      });
    } catch (err) {
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.disputes.syncError'),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUploadAttachment = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedDisputeId) {
      return;
    }

    setActionLoading(true);
    try {
      await uploadPaymentDisputeEvidenceAttachment(selectedDisputeId, file);
      await refreshDetail(selectedDisputeId);
      onFeedback?.({
        severity: 'success',
        message: t('paymentOperations.disputes.uploadSuccess'),
      });
    } catch (err) {
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.disputes.uploadError'),
      });
    } finally {
      setActionLoading(false);
      event.target.value = '';
    }
  };

  const columns = useMemo(() => [
    { field: 'storeName', header: t('paymentOperations.exceptions.columns.store'), render: (row) => row.storeName || '—' },
    {
      field: 'status',
      header: t('paymentOperations.exceptions.columns.status'),
      render: (row) => (
        <Chip
          size="small"
          label={row.status}
          color={row.requiresAction ? 'error' : 'default'}
        />
      ),
    },
    {
      field: 'amountMinorUnits',
      header: t('paymentOperations.disputes.columns.amount'),
      render: (row) => formatAmount(row.amountMinorUnits, row.currency),
    },
    {
      field: 'evidenceDueByUtc',
      header: t('paymentOperations.disputes.columns.deadline'),
      render: (row) => formatUtcDateTime(row.evidenceDueByUtc),
    },
    {
      field: 'providerDisputeId',
      header: t('paymentOperations.disputes.columns.disputeId'),
      render: (row) => row.providerDisputeId,
    },
  ], [t]);

  const evidenceSubmitted = Boolean(detail?.evidenceDraft?.submittedToStripeAtUtc);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <SwitchField
          checked={requiresActionOnly}
          onChange={(event) => {
            setRequiresActionOnly(event.target.checked);
            setPage(1);
          }}
          label={t('paymentOperations.disputes.requiresActionOnly')}
        />
      </Box>

      <DataTable
        data={rows}
        loading={loading}
        columns={columns}
        searchable={false}
        filterable={false}
        pagination
        rowsPerPage={ROWS_PER_PAGE}
        currentPage={page}
        totalCount={total}
        onPageChange={setPage}
        actionMenuItems={[{
          label: t('paymentOperations.disputes.manage'),
          tooltip: t('paymentOperations.disputes.manage'),
          onClick: (row) => openDetail(row),
        }]}
        emptyMessage={t('paymentOperations.empty')}
        noResultsMessage={t('paymentOperations.empty')}
        onRefresh={loadDisputes}
      />

      <Dialog
        open={Boolean(selectedDisputeId)}
        onClose={() => setSelectedDisputeId(null)}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>{t('paymentOperations.disputes.detailTitle')}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2">
                <strong>{t('paymentOperations.disputes.columns.disputeId')}:</strong> {detail.providerDisputeId}
              </Typography>
              <Typography variant="body2">
                <strong>{t('paymentOperations.disputes.columns.deadline')}:</strong> {formatUtcDateTime(detail.evidenceDueByUtc)}
              </Typography>
              <Typography variant="body2">
                <strong>{t('paymentOperations.exceptions.columns.status')}:</strong> {detail.status}
              </Typography>

              {evidenceSubmitted ? (
                <Alert severity="info">{t('paymentOperations.disputes.evidenceSubmitted')}</Alert>
              ) : null}

              <TextField
                label={t('paymentOperations.disputes.evidence.customerCommunication')}
                value={evidenceForm.customerCommunication || ''}
                onChange={(event) => setEvidenceForm((current) => ({ ...current, customerCommunication: event.target.value }))}
                multiline
                minRows={2}
                fullWidth
                disabled={evidenceSubmitted || actionLoading}
              />
              <TextField
                label={t('paymentOperations.disputes.evidence.cancellationPolicy')}
                value={evidenceForm.cancellationPolicy || ''}
                onChange={(event) => setEvidenceForm((current) => ({ ...current, cancellationPolicy: event.target.value }))}
                multiline
                minRows={2}
                fullWidth
                disabled={evidenceSubmitted || actionLoading}
              />
              <TextField
                label={t('paymentOperations.disputes.evidence.refundPolicy')}
                value={evidenceForm.refundPolicy || ''}
                onChange={(event) => setEvidenceForm((current) => ({ ...current, refundPolicy: event.target.value }))}
                multiline
                minRows={2}
                fullWidth
                disabled={evidenceSubmitted || actionLoading}
              />
              <TextField
                label={t('paymentOperations.disputes.evidence.serviceDocumentation')}
                value={evidenceForm.serviceDocumentation || ''}
                onChange={(event) => setEvidenceForm((current) => ({ ...current, serviceDocumentation: event.target.value }))}
                multiline
                minRows={2}
                fullWidth
                disabled={evidenceSubmitted || actionLoading}
              />
              <TextField
                label={t('paymentOperations.disputes.evidence.uncategorizedText')}
                value={evidenceForm.uncategorizedText || ''}
                onChange={(event) => setEvidenceForm((current) => ({ ...current, uncategorizedText: event.target.value }))}
                multiline
                minRows={2}
                fullWidth
                disabled={evidenceSubmitted || actionLoading}
              />
              <TextField
                label={t('paymentOperations.disputes.evidence.internalNotes')}
                value={evidenceForm.internalNotes || ''}
                onChange={(event) => setEvidenceForm((current) => ({ ...current, internalNotes: event.target.value }))}
                multiline
                minRows={2}
                fullWidth
                disabled={evidenceSubmitted || actionLoading}
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('paymentOperations.disputes.attachments')}
                </Typography>
                {(detail.evidenceAttachments || []).map((attachment) => (
                  <Typography key={attachment.id} variant="body2">
                    <a href={attachment.publicUrl} target="_blank" rel="noreferrer">{attachment.fileName}</a>
                  </Typography>
                ))}
                {!evidenceSubmitted ? (
                  <Button variant="outlined" component="label" disabled={actionLoading} sx={{ mt: 1 }}>
                    {t('paymentOperations.disputes.uploadAttachment')}
                    <input hidden type="file" onChange={handleUploadAttachment} />
                  </Button>
                ) : null}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDisputeId(null)} disabled={actionLoading}>
            {t('common.close')}
          </Button>
          <Button onClick={handleSync} disabled={actionLoading || !detail}>
            {t('paymentOperations.disputes.sync')}
          </Button>
          {!evidenceSubmitted ? (
            <>
              <Button onClick={handleSaveEvidence} disabled={actionLoading || !detail}>
                {t('paymentOperations.disputes.saveDraft')}
              </Button>
              <Button variant="contained" onClick={handleSubmitEvidence} disabled={actionLoading || !detail}>
                {actionLoading ? t('paymentOperations.disputes.submitting') : t('paymentOperations.disputes.submitEvidence')}
              </Button>
            </>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentDisputesSection;
